/**
 * Content detail: metadata first, the body only where the access rules allow.
 *
 *   - post (scheduled or published): the body shows, and the open is logged
 *   - draft: the body is hidden until a reason is entered
 *   - artifact (source): a redacted preview, the full body after a reason
 *
 * A revealed body counts down its 15 minutes and hides itself when they end.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EyeSlash, LockKey, ShieldCheck, Timer } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { type PillTone, StatusPill } from "@charisol-spark/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ModalSection } from "@/components/ui/ModalParts";
import {
  adminKeys,
  useAdminContentDetail,
  useRevealAdminContent,
} from "@/hooks/useAdmin";
import type { AdminContentDetail, AdminContentItem } from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import {
  RevealReasonDialog,
  type RevealReasonValues,
} from "./RevealReasonDialog";

const STATUS_TONES: Record<string, PillTone> = {
  published: "green",
  scheduled: "blue",
  draft: "amber",
  completed: "green",
  processing: "blue",
  failed: "red",
  error: "red",
};

const CLASS_LABELS: Record<AdminContentDetail["accessClass"], string> = {
  post: "Post",
  draft: "Draft",
  source: "Artifact",
};

function formatDateTime(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBytes(bytes: number | null | undefined) {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Seconds left until `expiresAt`, ticking once a second; null when no expiry. */
function useSecondsLeft(expiresAt: string | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  if (!expiresAt) return null;
  return Math.max(0, Math.ceil((Date.parse(expiresAt) - now) / 1000));
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  );
}

function BodyBlock({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  return (
    <pre
      className={`max-h-96 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-border/50 bg-muted/30 p-3 font-sans text-sm ${className ?? ""}`}
    >
      {text}
    </pre>
  );
}

function DetailBody({
  data,
  onRequestReveal,
}: {
  data: AdminContentDetail;
  onRequestReveal: () => void;
}) {
  const queryClient = useQueryClient();
  const { body } = data;
  const secondsLeft = useSecondsLeft(body.reveal?.expiresAt);
  const expired = body.access === "revealed" && secondsLeft === 0;

  // When the window ends, drop the cached body and ask the server again; it
  // answers with the locked view (and logs the new open).
  useEffect(() => {
    if (!expired) return;
    queryClient.invalidateQueries({
      queryKey: adminKeys.contentDetail(data.id, {
        userId: data.userId,
        type: data.contentType,
      }),
    });
  }, [expired, queryClient, data.id, data.userId, data.contentType]);

  if (body.access === "open") {
    return (
      <ModalSection id="body-heading" title="Content">
        <p className="text-xs text-muted-foreground">
          This {data.accessClass} is published or scheduled, so its text is
          visible. Opening it is logged.
        </p>
        {body.content ? (
          <BodyBlock text={body.content} />
        ) : (
          <p className="text-sm text-muted-foreground">
            This item has no body text.
          </p>
        )}
      </ModalSection>
    );
  }

  if (body.access === "revealed" && !expired) {
    return (
      <ModalSection id="body-heading" title="Content">
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning"
          role="timer"
          aria-label="Time left on the revealed body"
        >
          <span className="flex items-center gap-2">
            <Timer className="h-4 w-4" aria-hidden />
            Body open for{" "}
            <strong data-testid="reveal-countdown" className="tabular-nums">
              {formatCountdown(secondsLeft ?? 0)}
            </strong>
          </span>
          <span className="text-xs">Logged with your reason</span>
        </div>
        {body.content ? (
          <BodyBlock text={body.content} />
        ) : (
          <p className="text-sm text-muted-foreground">
            This item has no body text.
          </p>
        )}
      </ModalSection>
    );
  }

  const isSource = data.accessClass === "source";
  return (
    <ModalSection
      id="body-heading"
      title={isSource ? "Redacted preview" : "Content"}
    >
      {isSource && body.preview ? (
        <>
          <p className="text-xs text-muted-foreground">
            First 200 characters with emails, phone numbers, links and names
            after Dear, Hi or From: masked.
            {body.preview.truncated
              ? ` ${body.preview.hiddenChars.toLocaleString()} more characters are hidden.`
              : ""}
          </p>
          {body.preview.text ? (
            <BodyBlock text={body.preview.text} />
          ) : (
            <p className="text-sm text-muted-foreground">
              This item has no body text.
            </p>
          )}
        </>
      ) : (
        <div className="flex items-start gap-3 rounded-lg border border-dashed border-border bg-muted/20 p-4 text-sm">
          <EyeSlash className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <p className="text-muted-foreground">
            The body of a draft is private to its owner. You can see the
            details above freely; to read the text you need to give a reason,
            which is recorded.
          </p>
        </div>
      )}
      <div>
        <Button type="button" variant="outline" onClick={onRequestReveal}>
          <LockKey className="h-4 w-4" aria-hidden />
          Reveal {isSource ? "full " : ""}body
        </Button>
      </div>
    </ModalSection>
  );
}

function Metadata({ data }: { data: AdminContentDetail }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
      <Field label="Owner">
        <Link
          to={`/users/${data.userId}`}
          className="hover:text-primary hover:underline"
        >
          {data.owner.name || data.owner.email || data.userId}
        </Link>
      </Field>
      <Field label="Type">{CLASS_LABELS[data.accessClass]}</Field>
      {data.status && (
        <Field label="Status">
          <StatusPill
            tone={STATUS_TONES[data.status] ?? "neutral"}
            className="capitalize"
          >
            {data.status}
          </StatusPill>
        </Field>
      )}
      {data.persona && <Field label="Persona">{data.persona.name}</Field>}
      {data.platforms && data.platforms.length > 0 && (
        <Field label="Platforms">
          <span className="capitalize">{data.platforms.join(", ")}</span>
        </Field>
      )}
      <Field label="Words">
        {data.wordCount === null ? "—" : data.wordCount.toLocaleString()}
      </Field>
      {data.versionCount !== undefined && data.versionCount !== null && (
        <Field label="Versions">{data.versionCount}</Field>
      )}
      {data.source && (
        <>
          <Field label="Kind">
            <span className="capitalize">{data.source.kind ?? "—"}</span>
          </Field>
          {data.source.urlHost && (
            <Field label="Link host">{data.source.urlHost}</Field>
          )}
          <Field label="Size">{formatBytes(data.source.sizeBytes)}</Field>
        </>
      )}
      <Field label="Created">{formatDateTime(data.createdAt)}</Field>
      <Field label="Updated">{formatDateTime(data.updatedAt)}</Field>
      {data.scheduledAt && (
        <Field label="Scheduled">{formatDateTime(data.scheduledAt)}</Field>
      )}
      {data.publishedAt && (
        <Field label="Published">{formatDateTime(data.publishedAt)}</Field>
      )}
    </dl>
  );
}

function UsedBy({ data }: { data: AdminContentDetail }) {
  if (!data.source) return null;
  const used = data.source.usedByDrafts;
  return (
    <ModalSection id="used-by-heading" title="Drafts that use it">
      {used === null ? (
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t check which drafts use this artifact. Reopen it to
          retry.
        </p>
      ) : used.length === 0 ? (
        <p className="text-sm text-muted-foreground">No drafts use it yet.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {used.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate">{d.title || "Untitled"}</span>
              {d.status && (
                <StatusPill
                  tone={STATUS_TONES[d.status] ?? "neutral"}
                  className="capitalize"
                >
                  {d.status}
                </StatusPill>
              )}
            </li>
          ))}
        </ul>
      )}
    </ModalSection>
  );
}

/**
 * Detail view for one content item. The API needs the owner's userId and the
 * item's type to locate it (the id alone is not enough in a single-table
 * design keyed by owner) — both come off the list row.
 */
export function ContentDetailDialog({
  item,
  onOpenChange,
}: {
  item: AdminContentItem | null;
  onOpenChange: (open: boolean) => void;
}) {
  const type = item?.type as "source" | "draft" | undefined;
  const { data, isLoading, error } = useAdminContentDetail(
    item?.id,
    item && type ? { userId: item.userId, type } : undefined
  );
  const reveal = useRevealAdminContent(item?.id);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);

  function handleOpenChange(open: boolean) {
    if (!open) {
      setReasonOpen(false);
      setRevealError(null);
    }
    onOpenChange(open);
  }

  function handleReveal(values: RevealReasonValues) {
    if (!item || !type) return;
    setRevealError(null);
    reveal.mutate(
      { userId: item.userId, type, ...values },
      {
        onSuccess: () => setReasonOpen(false),
        onError: (err) =>
          setRevealError(
            getErrorMessage(
              err,
              "Couldn't open the body. Nothing was shown; try again."
            )
          ),
      }
    );
  }

  return (
    <>
      <Dialog open={item !== null} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{data?.title || item?.title || "Content"}</DialogTitle>
            <DialogDescription className="flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
              Details first. Draft and artifact text opens only with a reason,
              and every open is logged.
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Spinner size="md" />
            </div>
          ) : error ? (
            <Alert variant="error">
              {getErrorMessage(error, "Failed to load this content item.")}
            </Alert>
          ) : data ? (
            <div className="space-y-5">
              <Metadata data={data} />
              <UsedBy data={data} />
              <DetailBody
                data={data}
                onRequestReveal={() => {
                  setRevealError(null);
                  setReasonOpen(true);
                }}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {data && (
        <RevealReasonDialog
          open={reasonOpen}
          onOpenChange={setReasonOpen}
          subject={data.accessClass === "source" ? "artifact" : "draft"}
          minReasonLength={data.body.policy.minReasonLength}
          windowMinutes={data.body.policy.windowMinutes}
          isPending={reveal.isPending}
          error={revealError}
          onSubmit={handleReveal}
        />
      )}
    </>
  );
}
